import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';

export interface CategorySpendDataPoint {
  id: string;
  label: string;
  amount: number;
  year: number;
  month?: number;
}

interface CategorySpendingChartProps {
  data: CategorySpendDataPoint[];
  selectedPointId: string;
  isLoading: boolean;
  onPointSelect: (pointId: string) => void;
  className?: string;
  emptyMessage?: string;
  recenterToken?: number;
}

const CategorySpendingChart: React.FC<CategorySpendingChartProps> = ({
  data,
  selectedPointId,
  isLoading,
  onPointSelect,
  className,
  emptyMessage,
  recenterToken = 0,
}) => {
  const { resolvedTheme } = useTheme();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const centeredLayoutRef = useRef<{ width: number; token: number } | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  const pointSpacing = 68;
  const minPointsForWidthCalc = 6;
  const totalPointsWidth = Math.max(data.length, minPointsForWidthCalc) * pointSpacing;
  const svgHeight = 280;
  const labelHeight = 34;

  const selectedPointIndex = data.findIndex((point) => point.id === selectedPointId);

  const palette = useMemo(() => {
    if (resolvedTheme === 'dark') {
      return {
        line: '#4EA3FF',
        point: '#4EA3FF',
        pointInner: '#040506',
        month: 'text-zinc-500',
        monthSelected: 'text-zinc-100',
      };
    }
    return {
      line: '#2C84FF',
      point: '#2C84FF',
      pointInner: '#ffffff',
      month: 'text-zinc-500',
      monthSelected: 'text-foreground',
    };
  }, [resolvedTheme]);

  const padding = useMemo(() => {
    const sidePadding = containerWidth > 0 ? containerWidth / 2 : pointSpacing / 2;
    return {
      top: 30,
      right: Math.max(pointSpacing / 2, sidePadding),
      bottom: 44,
      left: Math.max(pointSpacing / 2, sidePadding),
    };
  }, [containerWidth, pointSpacing]);

  const calculatedSvgWidth = totalPointsWidth + padding.left + padding.right;
  const chartHeight = svgHeight - padding.top - padding.bottom;

  const xScale = useCallback((index: number) => {
    return padding.left + pointSpacing / 2 + index * pointSpacing;
  }, [padding.left, pointSpacing]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    setContainerWidth(container.clientWidth);

    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === container) {
          setContainerWidth(entry.contentRect.width);
        }
      });
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || containerWidth <= 0 || selectedPointIndex === -1) {
      return;
    }

    const previous = centeredLayoutRef.current;
    if (previous?.width === containerWidth && previous.token === recenterToken) return;
    centeredLayoutRef.current = { width: containerWidth, token: recenterToken };

    const targetX = xScale(selectedPointIndex);
    const scrollLeft = Math.max(0, targetX - containerWidth / 2);

    container.scrollTo({
      left: scrollLeft,
      behavior: 'auto',
    });
    container.style.opacity = '1';
    // Match CashFlow behavior: recenter only when layout width is known/changes.
    // Avoid recentering on each selected-month update to prevent jitter while scrolling.
  }, [containerWidth, recenterToken, selectedPointIndex, xScale]);

  const findClosestDataPoint = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || containerWidth <= 0) {
      return null;
    }

    const scrollCenter = container.scrollLeft + containerWidth / 2;
    let closestIndex = -1;
    let minDistance = Infinity;

    for (let index = 0; index < data.length; index += 1) {
      const pointX = xScale(index);
      const distance = Math.abs(pointX - scrollCenter);
      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = index;
      }
    }

    if (closestIndex === -1) {
      return null;
    }

    return data[closestIndex];
  }, [containerWidth, data, xScale]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || data.length === 0 || containerWidth <= 0) {
      return;
    }

    const handleScroll = () => {
      const closest = findClosestDataPoint();
      if (!closest) {
        return;
      }
      if (closest.id !== selectedPointId) {
        onPointSelect(closest.id);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [containerWidth, data, findClosestDataPoint, onPointSelect, selectedPointId]);

  return (
    <div
      ref={scrollContainerRef}
      className={`overflow-x-auto scrollbar-hide w-full relative snap-x snap-mandatory ${className ?? ''}`}
      style={{ scrollbarWidth: 'none', opacity: 0 }}
    >
      {isLoading || data.length === 0 || containerWidth <= 0 ? (
        <p className="text-muted-foreground text-center absolute inset-0 flex items-center justify-center">
          {isLoading ? 'Loading chart...' : emptyMessage ?? 'No category spend data.'}
        </p>
      ) : (
        <>
          <svg
            width={calculatedSvgWidth}
            height={svgHeight}
            viewBox={`0 0 ${calculatedSvgWidth} ${svgHeight}`}
            preserveAspectRatio="xMidYMid meet"
            className="block relative z-0"
          >
            {(() => {
              const maxValue = Math.max(...data.map((point) => point.amount), 1);
              const yScale = (value: number) =>
                padding.top + chartHeight - (value / maxValue) * chartHeight;

              const linePoints = data
                .map((point, index) => `${xScale(index)},${yScale(point.amount)}`)
                .join(' ');

              return (
                <>
                  {data.length > 1 && (
                    <polyline
                      fill="none"
                      stroke={palette.line}
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      points={linePoints}
                    />
                  )}

                  {data.map((point, index) => {
                    const xPos = xScale(index);
                    const yPos = yScale(point.amount);
                    const isSelected = point.id === selectedPointId;

                    return (
                      <g key={point.id}>
                        <circle
                          cx={xPos}
                          cy={yPos}
                          r={isSelected ? 9 : 7}
                          fill={palette.point}
                          stroke={isSelected ? '#8BC2FF' : 'transparent'}
                          strokeWidth={isSelected ? 1.5 : 0}
                        />
                        <circle cx={xPos} cy={yPos} r={isSelected ? 4 : 3} fill={palette.pointInner} />
                      </g>
                    );
                  })}
                </>
              );
            })()}
          </svg>

          {data.map((point, index) => {
            const xPos = xScale(index);
            const isSelected = point.id === selectedPointId;

            return (
              <div
                key={`label-${point.id}`}
                className="absolute flex items-center justify-center snap-center"
                style={{
                  left: `${xPos}px`,
                  bottom: '0px',
                  width: '52px',
                  height: `${labelHeight}px`,
                  transform: 'translateX(-50%)',
                  zIndex: 1,
                }}
              >
                <span className={`text-sm ${isSelected ? `${palette.monthSelected} font-semibold` : palette.month}`}>
                  {point.label}
                </span>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default CategorySpendingChart;
